/** Loads an image object URL, resolving when ready, rejecting on error/timeout. */
function waitUntilImageIsLoaded(imageUrl: string, timeout = 10000): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const image = new Image();
    image.src = imageUrl;
    const cleanup = () => {
      clearTimeout(timeoutId);
      image.onload = null;
      image.onerror = null;
      URL.revokeObjectURL(imageUrl);
    };
    const timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error("Image loading timed out."));
    }, timeout);
    image.onload = () => { cleanup(); resolve(); };
    image.onerror = () => { cleanup(); reject(new Error("Image failed to load.")); };
  });
}

/** Produces a JPEG data URL thumbnail for an image File. HEIC/HEIF conversion is
 *  intentionally disabled (matches the legacy hotfix that prevented an iOS 18 crash). */
export async function getThumbnailAsDataUrl(
  file: File,
  width?: number,
  height?: number,
  quality = 0.7,
): Promise<string> {
  if (file.type === "image/heif" || file.type === "image/heic") {
    throw new Error("Hotfix: Converting of HEIC/HEIF images currently disabled.");
  }
  const imageUrl = URL.createObjectURL(file);
  const image = new Image();
  image.src = imageUrl;
  await waitUntilImageIsLoaded(imageUrl);

  const canvas = document.createElement("canvas");
  if (width && height) {
    canvas.width = width;
    canvas.height = height;
  } else if (width) {
    canvas.width = width;
    canvas.height = Math.floor((image.height * width) / image.width);
  } else if (height) {
    canvas.width = Math.floor((image.width * height) / image.height);
    canvas.height = height;
  } else {
    canvas.width = image.width;
    canvas.height = image.height;
  }
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not get 2d canvas context");
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", quality);
}
